import React from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { env } from '../env';

type CountryCode = import('react-phone-number-input').Country;

export interface PhoneFieldProps {
  label?: React.ReactNode;
  value?: string;
  onChange: (value?: string) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: React.ReactNode;
  errorText?: React.ReactNode;
  id?: string;
  name?: string;
  placeholder?: string;
  defaultCountry?: CountryCode;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  hideValidationMessage?: boolean;
  helperPrefix?: React.ReactNode;
}

const baseInputClasses =
  'w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900/40 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-gray-100 dark:disabled:bg-slate-800';

export const PhoneField: React.FC<PhoneFieldProps> = ({
  label,
  value,
  onChange,
  required,
  disabled,
  helperText,
  errorText,
  id,
  name,
  placeholder = 'Include country code e.g. +15551234567',
  defaultCountry = 'US',
  containerClassName = '',
  labelClassName = '',
  inputClassName,
  hideValidationMessage,
  helperPrefix,
}) => {
  const validationDisabled = env.REACT_APP_DISABLE_PHONE_VALIDATION === 'true'
    || env.REACT_APP_SMS_DISABLED === 'true';
  const isValid = validationDisabled
    ? Boolean(value || !required)
    : value ? isValidPhoneNumber(value) : !required;
  const mergedInputClass = inputClassName ? `${baseInputClasses} ${inputClassName}` : baseInputClasses;

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={id || name} className={`block text-sm font-medium mb-2 ${labelClassName}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <PhoneInput
        international
        defaultCountry={defaultCountry}
        value={value}
        onChange={onChange}
        disabled={disabled}
        numberInputProps={{
          id,
          name,
          required,
          className: mergedInputClass,
          placeholder,
        }}
        className="w-full"
      />
      {!hideValidationMessage && !validationDisabled && value && !isValid && (
        <p className="mt-1 text-xs text-red-500">Enter a valid phone number with country code.</p>
      )}
      {helperText && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
          {helperPrefix}
          {helperText}
        </p>
      )}
      {errorText && <p className="mt-1 text-xs text-red-500">{errorText}</p>}
    </div>
  );
};
